import URI from 'urijs';

export interface AjaxPostRequest<TData = unknown> {
  url: string;
  data?: TData;
}

export interface AjaxPromise {
  post<TResult = unknown, TData = unknown>(request: AjaxPostRequest<TData>): Promise<TResult>;
}

export interface PidlServiceOptions {
  ccBaseUrl: string;
  ajaxPromise: AjaxPromise;
}

export interface PidlAddress {
  address_line1: string;
  address_line2: string;
  city: string;
  region: string;
  postal_code: string;
  country: string;
}

export interface GetAddressListRequest {
  countryCode: string;
  silent?: boolean;
}

export interface AddOrUpdatePIData {
  cid: number;
  aid?: number;
  piid: number;
  middleTierPiId?: number;
  rurl?: string;
  apType?: string;
  sessionId?: string;
}

export interface AddOrUpdatePIRequest<TData = AddOrUpdatePIData> {
  data: TData;
}

/**
 * List of available security actions that can be provided to the getPidlInfo function
 */
export const securityAction = {
  PaymentInstrumentAdd: 'PaymentInstrAdd',
  PaymentInstrumentUpdate: 'PaymentInstrUpdate',
  PaymentInstrumentDelete: 'PaymentInstrDelete',
  PaymentInstrumentFetch: 'PaymentInstrFetch',
  PaymentInstrValidate: 'PaymentInstrValidate',
  PaymentInstrLoad: 'PaymentInstrLoad',
} as const;

export type SecurityAction = typeof securityAction[keyof typeof securityAction];

export interface GetPidlRequest {
  middleTierPiId: number;
  rurl?: string;
  failureRurl?: string;
  securityAction?: SecurityAction;
}

export interface GetPidlResult {
  AuthorizationToken: string;
  FailureUrl: string;
  SuccessUrl: string;
  UserId: string;
}

export default class {
  addressLoadUrl: string;
  ajaxPromise: AjaxPromise;
  getPidlInfoUrl: string;
  successUrl: string;

  constructor({
    ccBaseUrl,
    ajaxPromise,
  }: PidlServiceOptions) {
    const paymentUri = new URI(ccBaseUrl).segment('paymentmethods');
    this.ajaxPromise = ajaxPromise;
    this.addressLoadUrl = paymentUri.clone().segment('getpiaddressesbycustomerid').toString();
    this.getPidlInfoUrl = paymentUri.clone().segment('getpidlsmart').toString();
    this.successUrl = new URI(ccBaseUrl).segment('payment').segment('successpidlsmart').toString();
  }

  /**
   * @typedef PidlAddress
   * @type {object}
   * @param {string} address_line1 - The first line of the address.
   * @param {string} address_line2 - The second line of the address.
   * @param {string} city - The city of the address.
   * @param {string} region - The state/province of the address.
   * @param {string} postal_code - The post code of the address.
   * @param {string} country - The country code of the address.
   */

  /**
   * Get the list of addresses for a given country code.
   * @param {string} countryCode - The country to get addresses for.
   * @param {boolean} silent - Whether or not to show/hide the loading panel at the end of the ajax cal.
   * @return {Promise<PidlAddress[], AjaxError|JsonError>} - The result of the call.
   */
  getAddressList = ({
    countryCode,
  }: GetAddressListRequest) => this.ajaxPromise.post<PidlAddress[]>({
    url: this.addressLoadUrl,
    data: { countryCode },
  });

  /**
   * Add PI to MT, or update the value of PI in MT.
   * @param {int} data.cid - Id of the customer adding/editing a pi.
   * @param {int} [data.aid] - Id of the account pi is added to.
   * @param {int} data.piid - The id of the pi in CP.
   * @param {int} [data.middleTierPiId] - The id of the pi in MT.
   * @param {string} [data.rurl] - The url to redirect to after adding/editing the pi.
   * @param {string} [data.apType] - The payment type of the pi.
   * @param {string} [data.sessionId] - The session ID from the PSD2 challenge.
   * @return {Promise<int, AjaxError|JsonError>} - The result of the call, resolves to paymentinstrument id.
   */
  addOrUpdatePI = <TData = AddOrUpdatePIData>({
    data,
  }: AddOrUpdatePIRequest<TData>) => this.ajaxPromise.post<number, TData>({
    url: this.successUrl,
    data,
  });

  /**
   * @typedef GetPidlRequest
   * @type {object}
   * @param {int} middleTierPiId - The MT payment instrument Id.
   * @param {string} [rurl] - Return URL.
   * @param {string} [failureRurl] - Failure return URL.
   * @param {string} [securityAction] - Security action paramter.
   */

  /**
   * @typedef GetPidlResult
   * @type {object}
   * @param {string} AuthorizationToken - Token authorizing pidl to update the given user's account.
   * @param {string} FailureUrl - Url to redirect to after a failed pi add.
   * @param {string} SuccessUrl - Url to redirect to after a successful pi add.
   * @param {string} UserId - Id of the current customer in cp's system.
   */

  /**
   * Get the required info for initializing pidl.
   * @param {GetPidlRequest} data - data to pass in getpidl call.
   * @return {Promise<GetPidlResult, AjaxError|JsonError>} - The result of the call.
   */
  getPidlInfo = (data?: GetPidlRequest) => this.ajaxPromise.post<GetPidlResult, GetPidlRequest | undefined>({
    url: this.getPidlInfoUrl,
    data,
  });
}
